import IosShareIcon from '@mui/icons-material/IosShare';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useDrawMapStore } from "../..";
import useClosable from "../../hooks/useClosable";
import DetailsType from "../../interfaces/DetailsType";
import { ClosableOrder } from "../../store/ClosableStore";
import { DrawMapMode } from "../../store/DrawMapStore";
import commonStyles from '../Common.module.css';
import detailsStyles from '../Details.module.css';
import DetailsPanel from "../DetailsPanel";
import CompanyIcon from "../icons/CompanyIcon";
import DealIcon from "../icons/DealIcon";
import LeadIcon from "../icons/LeadIcon";
import LeaseCompIcon from "../icons/LeaseCompIcon";
import LocationIcon from "../icons/LocationIcon";
import SaleCompIcon from "../icons/SaleCompIcon";
import TenantInMarketIcon from "../icons/TenantInMarketIcon";
import IconWithTooltip from "../IconWithTooltip";
import SegmentedButtons from "../SegmentedButtons";
import InsightsCompaniesTab from "./InsightsCompaniesTab";
import InsightsDealsTab from "./InsightsDealsTab";
import InsightsExportData from "./InsightsExportData";
import InsightsLeadsTab from "./InsightsLeadsTab";
import InsightsLeaseCompsTab from "./InsightsLeaseCompsTab";
import InsightsRecordedSalesTab from "./InsightsRecordedSalesTab";
import InsightsSaleCompsTab from "./InsightsSaleCompsTab";
import InsightsTenantsInMarketTab from "./InsightsTenantsInMarketTab";

function Insights() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const drawMapStore = useDrawMapStore();
    const [insightTab, setInsightTab] = useState(InsightTab.Companies);
    const [companyCount, setCompanyCount] = useState(0);
    const [dealCount, setDealCount] = useState(0);
    const [leadCount, setLeadCount] = useState(0);
    const [leaseCompCount, setLeaseCompCount] = useState(0);
    const [saleCompCount, setSaleCompCount] = useState(0);
    const [recordedSaleCount, setRecordedSaleCount] = useState(0);
    const [tenantInMarketCount, setTenantInMarketCount] = useState(0);
    const [isExportWindowOpen, setIsExportWindowOpen] = useState(false);
    const segmentedButtons = useMemo(() => [
        {
            value: InsightTab.Companies,
            label: (
                <IconWithTooltip title="Companies" count={companyCount}>
                    <CompanyIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: InsightTab.Deals,
            label: (
                <IconWithTooltip title="Deals" count={dealCount}>
                    <DealIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: InsightTab.Leads,
            label: (
                <IconWithTooltip title="Leads" count={leadCount}>
                    <LeadIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: InsightTab.LeaseComps,
            label: (
                <IconWithTooltip title="Lease Comps" count={leaseCompCount}>
                    <LeaseCompIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: InsightTab.SaleComps,
            label: (
                <IconWithTooltip title="Sale Comps" count={saleCompCount}>
                    <SaleCompIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: InsightTab.RecordedSales,
            label: (
                <IconWithTooltip title="Recorded Sales" count={recordedSaleCount}>
                    <LocationIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        {
            value: InsightTab.TenantsInMarket,
            label: (
                <IconWithTooltip title="Tenants in Market" count={tenantInMarketCount}>
                    <TenantInMarketIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
    ], [companyCount, dealCount, leadCount, leaseCompCount, saleCompCount, recordedSaleCount, tenantInMarketCount]);

    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    useEffect(() => {
        drawMapStore.drawInsights(state?.insightsShape);
        return () => drawMapStore.clear();
    }, []);

    useEffect(() => {
        if (state?.insightsShape)
            drawMapStore.drawInsights(state.insightsShape);
    }, [state?.insightsShape]);

    useEffect(() => {
        if (drawMapStore.mode === DrawMapMode.None)
            close();
    }, [drawMapStore.mode]);

    if (drawMapStore.mode !== DrawMapMode.Insights || !drawMapStore.result)
        return null;

    return (
        <>
            <DetailsPanel type={DetailsType.Insights} onClose={close}>
                <div className={detailsStyles.header}>
                    <div className={detailsStyles.headerGroup}>
                        <div className={detailsStyles.title}>
                            <span className={commonStyles.primary}>Insights</span>
                        </div>
                    </div>
                    <div className={detailsStyles.headerGroup}>
                        <button className={commonStyles.navigationButton} onClick={() => setIsExportWindowOpen(true)}>
                            <IosShareIcon /> Export to email
                        </button>
                    </div>
                </div>
                <SegmentedButtons
                    value={insightTab}
                    onChange={setInsightTab}
                    options={segmentedButtons}
                    className={detailsStyles.segmentedButtons}
                />
                <div className={detailsStyles.body}>
                    <InsightsCompaniesTab visible={insightTab === InsightTab.Companies} setEntityCount={setCompanyCount} />
                    <InsightsDealsTab visible={insightTab === InsightTab.Deals} setEntityCount={setDealCount} />
                    <InsightsLeadsTab visible={insightTab === InsightTab.Leads} setEntityCount={setLeadCount} />
                    <InsightsLeaseCompsTab visible={insightTab === InsightTab.LeaseComps} setEntityCount={setLeaseCompCount} />
                    <InsightsSaleCompsTab visible={insightTab === InsightTab.SaleComps} setEntityCount={setSaleCompCount} />
                    <InsightsRecordedSalesTab visible={insightTab === InsightTab.RecordedSales} setEntityCount={setRecordedSaleCount} />
                    <InsightsTenantsInMarketTab visible={insightTab === InsightTab.TenantsInMarket} setEntityCount={setTenantInMarketCount} />
                </div>
            </DetailsPanel>
            {isExportWindowOpen && <InsightsExportData onClose={() => setIsExportWindowOpen(false)} />}
        </>
    );
}

export default observer(Insights);

enum InsightTab {
    Companies,
    Deals,
    Leads,
    LeaseComps,
    SaleComps,
    RecordedSales,
    TenantsInMarket,
}
